// frontend/src/lib/avatarUrl.js
//
// Shared avatar URL resolver. The backend returns avatar URLs as a RELATIVE
// path (e.g. "/api/avatars/{id}?v=3"). Rendered directly in an <img src>, a
// relative path resolves against the SPA document origin (openrunsusa.com on
// web / iOS Live Mode), which does NOT proxy /api/* to the Render backend, so
// the image returns an empty 200 and the avatar appears "broken". This helper
// validates supported avatar URLs consistently across screens.
// Absolute HTTP(S), supported backend avatar paths, raster data previews and
// blob previews are supported. Invalid values return undefined for a placeholder.
// Backend-relative paths use the backend origin; cache-busting queries survive.
export function resolveAvatarUrl(url) {
  if (!url || typeof url !== 'string') return undefined;
  if (url.trim() !== url || /[\s\u0000-\u001f\u007f\\]/.test(url)) return undefined;
  // Validate escapes without replacing the original encoded path or query.
  try { decodeURI(url); } catch { return undefined; }
  const webUrl = value => {
    // URL() repairs empty authorities such as https:///host; reject them first.
    if (!/^https?:\/\/[^/?#\s\\]+(?:[/?#]|$)/i.test(value)) return false;
    try {
      const parsed = new URL(value);
      return ['https:', 'http:'].includes(parsed.protocol) && parsed.hostname &&
        !parsed.username && !parsed.password;
    } catch { return false; }
  };
  if (webUrl(url)) return url;
  if (/^\/(?:api\/avatars|api\/profile-images|profile-images)\/[^/?#]+/.test(url)) {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (!webUrl(base)) return undefined;
    const resolved = new URL(url, base);
    if (!/^\/(?:api\/avatars|api\/profile-images|profile-images)\/[^/]+/.test(resolved.pathname)) return undefined;
    return resolved.href;
  }
  if (url.startsWith('blob:')) {
    const inner = url.slice(5);
    if (webUrl(inner) && new URL(inner).pathname !== '/') return url;
    if (/^null\/[a-f0-9-]{36}$/i.test(inner)) return url;
  }
  if (/^data:image\/(?:png|jpeg|gif|webp|avif);base64,[A-Za-z0-9+/]+={0,2}$/.test(url)) return url;
  return undefined;
}
